import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {FormBuilder} from '@angular/forms';
import {Store} from "@ngrx/store";
import * as fromStore from '../../../../../store';
import {GetResellerMainDomain} from '../../../../../store';
import {filter, first} from "rxjs/operators";
import {MainDomainI} from "./custom-domain.interface";
import {DbModalService} from "../../../../../modules/modal/modal";
import {SslCertificateModalComponent} from "./components/ssl-certificate-modal/ssl-certificate-modal.component";
import {
  CustomDomainSuccessModalComponent
} from "./components/custom-domain-success-modal/custom-domain-success-modal.component";
import {CustomDomainAddModalComponent} from "./components/custom-domain-add-modal/custom-domain-add-modal.component";
import {
  CustomDomainNameserversModalComponent
} from "./components/custom-domain-nameservers-modal/custom-domain-nameservers-modal.component";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {ResellerApiService} from "../../services/10web-reseller-api.service";
import {
  HostingDomainsZoneDataMapper
} from "../../../../../websites/website/hosting/domains/hosting-domains-zone-mapper";
import {HostingService} from "../../../../../websites/website/hosting/hosting.service";
import {combineLatest, switchMap} from "rxjs";
import {UserSubscription, UserSubscriptionCategory} from "../../../../../user/user-subscription";
import {User} from "../../../../../user/user";
import {NotifierComponent} from "../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../shared/components/notifier/notification-type";
import {DbModalRef} from "../../../../../modules/modal/modal-ref";
import {ActivityAction, ActivityStatus} from "../../../../../activities/activity";
import {ActivityNotificationService} from "../../../../../activity-notifications/activity-notification.service";

@Component({
    selector: 'app-custom-domain',
    templateUrl: './custom-domain.component.html',
    styleUrls: ['./custom-domain.component.scss'],
    standalone: false
})
export class CustomDomainComponent implements OnInit {
  protected readonly UserSubscriptionCategory = UserSubscriptionCategory;
  mainDomain: MainDomainI;
  domainStatus: string;
  domainNotFound = false;
  troubleshootLoading = false;
  private _destroyRef = inject(DestroyRef);
  nameservers = [];
  mainLoading = true;
  pointed = false;
  currentSubscription: UserSubscription;
  user: User;
  loadingModal: DbModalRef;

  constructor(private fb: FormBuilder,
              private _store: Store,
              private _resellerApi: ResellerApiService,
              private _hostingService: HostingService,
              private _activityNotificationService: ActivityNotificationService,
              private _modalService: DbModalService) {
  }

  ngOnInit(): void {
      this._store.dispatch(new GetResellerMainDomain());
      this._store.dispatch(new fromStore.LoadZones());
      this.getDomain();
      this._loadSubData();
      this.listenSSLGeneration();
  }


  public openNameservers(isDefault = false) {
      const modal = this._modalService.open(CustomDomainNameserversModalComponent, {
          hasCloseBtn: false,
          backdropClass: 'dark'
      });
      modal.componentInstance.nameservers = this.nameservers;
      modal.componentInstance.withError = !isDefault && !this.pointed;
      modal.componentInstance.pointed = this.pointed;
      modal.componentInstance.default = isDefault;
  }

  private openSllLoading() {
      return this._modalService.open(SslCertificateModalComponent, {
          hasCloseBtn: false,
          backdrop: 'static',
          backdropClass: 'dark'
      });
  }

  private openAddDomainSuccess() {
      this._modalService.open(CustomDomainSuccessModalComponent, {hasCloseBtn: false, backdropClass: 'dark'});
  }

  public openAddDomain() {
    if (this.currentSubscription?.isAgency || this.currentSubscription?.boosterCategory === UserSubscriptionCategory.AGENCY || this.currentSubscription?.apiResellerPro) {
      const modal = this._modalService.open(CustomDomainAddModalComponent, {backdropClass: 'dark'});
      modal.closed
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(value => {
          if (value) {
            this.addDomain({domain_name: value});
          }
        });
    }
  }

  private getZones() {
      this._hostingService.getZones()
          .pipe(filter((response): boolean => !!response),
              takeUntilDestroyed(this._destroyRef),
              first())
          .subscribe((response) => {
              if (response.success) {
                  const data = response.data.map((zone) => HostingDomainsZoneDataMapper.map(zone));
                  this.nameservers = data.find(zone => zone.name === this.mainDomain.main_domain).nameservers;
              }
          });
  }

  private getDomain() {
      this._store.select(fromStore.getMainDomain)
          .pipe(
              first(domain => !!domain),
              takeUntilDestroyed(this._destroyRef)
          ).subscribe(domain => {
              this.mainDomain = domain;
              this.domainStatus = domain.status;
              this.getZones();
              this.troubleshootDomain(true);
              this.mainLoading = false;
          });
      this._store.select(fromStore.getError).pipe(
          takeUntilDestroyed(this._destroyRef)
      ).subscribe(err => {
          if (err) {
              this.domainNotFound = err === 'Main domain not found.';
              this.mainLoading = false;
          }
      });
  }

  public addDomain(data) {
      this._resellerApi.addDomain(data)
          .pipe(takeUntilDestroyed(this._destroyRef))
          .subscribe(value => {
              console.log(value);
              if (value?.data) {
                  if (value.data.nameservers) {
                      this.nameservers = JSON.parse(value.data.nameservers);
                      this.mainDomain = {main_domain: data.domain_name, status: 'inactive'};
                      this.domainNotFound = false;
                      this.openNameservers(true);
                  }
              }
              if (value && !value.success) {
                  NotifierComponent.notify(new Notification(value.message, NotificationType.DANGER));
              }
          });
  }


  troubleshootDomain(initial = false) {
      this.troubleshootLoading = true;
      this._resellerApi.troubleshootMainDomain().pipe(
          takeUntilDestroyed(this._destroyRef)
      ).subscribe(value => {
          if (value) {
              this.pointed = !!value.data?.tenweb_ns;
              if (this.pointed && !initial) {
                  this._store.dispatch(new GetResellerMainDomain());
              }
              this.troubleshootLoading = false;
          }
      });
  }

  removeDomain() {
      this._resellerApi.removeDomain()
          .pipe(first(value => !!value))
          .subscribe(value => {
              this.mainDomain = null;
              this.domainNotFound = true;
          });
  }

  generateSSL() {
      this.loadingModal = this.openSllLoading();
      this._resellerApi.generateSLL().pipe(first(value => !!value))
          .subscribe(value => {
              if (!value?.success) {
                  NotifierComponent.notify(new Notification(value.message, NotificationType.DANGER));
              }
          });
  }

private _loadSubData() {
  this._store.select(fromStore.getUserLoaded)
    .pipe(
      first(loaded => loaded),
      takeUntilDestroyed(this._destroyRef),
      switchMap(() => combineLatest([
        this._store.select(fromStore.getUser),
        this._store.select(fromStore.getUserSubscription),
      ])),
      first(([_, userSubscription]) => !!userSubscription)
    )
    .subscribe(([user, userSubscription]) => {
      this.user = user;
      this.currentSubscription = userSubscription;
    });
}

  private listenSSLGeneration() {
      this._activityNotificationService.subscribeToActivities(true)
          .pipe(
              takeUntilDestroyed(this._destroyRef),
              filter((data: any) => {
                  return data && [ActivityAction.SSL_CERTIFICATE_GENERATION].includes(data.action) &&
                      data.status === ActivityStatus.SUCCESS;
              })
          ).subscribe((data) => {
          if (!data) {
              return;
          }
          this.loadingModal?.close();
          this.openAddDomainSuccess();
          this.mainDomain = {...this.mainDomain, status: 'active'};
          this.domainStatus = 'active';
          this.pointed = true;
      });
  }
}
